import { queryOptions } from '@tanstack/react-query';
import { authedGet } from '@/lib/api';

export interface IndexQueryArgs {
  /** Query-key slot 0 — MUST equal the prefix the entity's mutations invalidate. */
  entity: string;
  organizationId: string | undefined;
  /** Full endpoint path, e.g. `/api/v1/expenses/orgs/${orgId}/expenses/index`. */
  path: string;
  /**
   * Normalized filters: null (or '') = absent — both are skipped in the
   * query string, since index query schemas validate present params (a sent
   * `?projectId=` would 400 where an omitted one succeeds). The object
   * becomes queryKey slot [3] verbatim, so keep its shape stable.
   */
  params?: Record<string, string | null>;
  staleTime?: number;
}

/**
 * useQuery options for a slim "index" read — the COMPLETE org-scoped list an
 * entity grid does client-side search/sort/filter over. One shape for every
 * `use<Entity>Index` hook:
 *
 * - key `[entity, orgId, 'index', params]` so the entity's existing
 *   org-prefixed mutation invalidations refresh it;
 * - `{ items }` unwrap from the paged index endpoint;
 * - disabled (and an early `[]` from the queryFn) without an org;
 * - previous-page placeholder that never leaks across orgs.
 *
 * This is an options builder, not a hook: the hand-written hooks stay
 * rules-of-hooks-trivial and keep their entity-specific liveness contracts
 * (which mutations invalidate them) in their own JSDoc.
 */
export function indexQueryOptions<Item>(args: IndexQueryArgs) {
  const { entity, organizationId, path, params, staleTime = 60_000 } = args;
  return queryOptions({
    queryKey: [entity, organizationId, 'index', params ?? {}],
    queryFn: async (): Promise<Item[]> => {
      if (!organizationId) return [];
      const search = new URLSearchParams();
      for (const [name, value] of Object.entries(params ?? {})) {
        if (value != null && value !== '') search.set(name, value);
      }
      const qs = search.toString();
      const result = await authedGet<{ items: Item[] }>(
        `${path}${qs ? `?${qs}` : ''}`,
      );
      return result.items;
    },
    enabled: !!organizationId,
    staleTime,
    // No grid flash while a refetch or filter change is in flight — but only
    // within the SAME org. On an org switch without a remount (browser
    // back/forward between /org/A and /org/B — OrgLayout has no key={orgId}),
    // a bare keepPreviousData would briefly render org A's index as org B's
    // success data. Keep the previous page only when the previous query's
    // orgId (key slot [1]) matches.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === organizationId ? previousData : undefined,
  });
}
